// Phase 3 — Default pricing mode selector + global safety caps.
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Save } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import {
  useShopPricingSettings,
  useUpsertPricingSettings,
} from '@/hooks/pricing/useShopPricingConfig';
import type { PricingMode } from '@/lib/parts-financial/calculator';

interface Props { shopId: string; }

export function GlobalModeSection({ shopId }: Props) {
  const settingsQ = useShopPricingSettings(shopId);
  const upsert = useUpsertPricingSettings();

  const [mode, setMode] = useState<PricingMode>('matrix');
  const [maxMult, setMaxMult] = useState(5.0);
  const [maxMarkup, setMaxMarkup] = useState(400.0);

  useEffect(() => {
    if (!settingsQ.data) return;
    setMode(settingsQ.data.default_mode);
    setMaxMult(Number(settingsQ.data.global_max_multiplier));
    setMaxMarkup(Number(settingsQ.data.global_max_markup_pct));
  }, [settingsQ.data]);

  const handleSave = async () => {
    try {
      await upsert.mutateAsync({
        shop_id: shopId,
        default_mode: mode,
        global_max_multiplier: maxMult,
        global_max_markup_pct: maxMarkup,
      });
      toast({ title: 'Pricing defaults saved' });
    } catch (e: any) {
      toast({ title: 'Save failed', description: e?.message, variant: 'destructive' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Default Pricing Mode</CardTitle>
        <CardDescription>
          Sets the suggestion engine for parts that don't have a category override.
          Suggestions only — manual sell prices on parts lines remain authoritative.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Default mode</Label>
            <Select value={mode} onValueChange={(v) => setMode(v as PricingMode)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="flat">Flat markup</SelectItem>
                <SelectItem value="matrix">Tiered matrix</SelectItem>
                <SelectItem value="optiguard">OptiGuard adaptive</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Global max multiplier</Label>
            <Input
              type="number" step="0.1" min="1"
              value={maxMult}
              onChange={(e) => setMaxMult(Number(e.target.value))}
            />
          </div>
          <div className="space-y-2">
            <Label>Global max markup %</Label>
            <Input
              type="number" step="1" min="0"
              value={maxMarkup}
              onChange={(e) => setMaxMarkup(Number(e.target.value))}
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={upsert.isPending} size="sm">
            {upsert.isPending
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Save className="h-4 w-4 mr-2" />}
            Save defaults
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
